import Vue from 'vue';
import Router from 'vue-router';
import i18n, {
  loadLanguageAsync
} from "../lang";
import store from '../store';
import NProgress from 'nprogress';
import onlinePayouts from '../store/genreports/online-payouts';

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer');

// Views
const Login = () => import('@/views/auth/Login');
const TwoFA = () => import('@/views/auth/TwoFA');
const Home = () => import('@/views/home/Home');
const Users = () => import('@/views/user/Users');
const User = () => import('@/views/user/User');
const Roles = () => import('@/views/role/Roles');
const Role = () => import('@/views/role/Role');
const Permissions = () => import('@/views/permissions/Permissions');
const Shops = () => import('@/views/shop/Shops');
const Shop = () => import('@/views/shop/Shop');
const Devices = () => import('@/views/device/Devices');
const Device = () => import('@/views/device/Device');
const ShopGroups = () => import('@/views/shop-group/ShopGroups');
const ShopGroup = () => import('@/views/shop-group/ShopGroup');
const Tenants = () => import('@/views/tenant/Tenants');
const Tenant = () => import('@/views/tenant/Tenant');
const Registry = () => import('@/views/registry/Registry');
const Profile = () => import('@/views/me/Profile');
const Countries = () => import('@/views/country/Countries');
const Country = () => import('@/views/country/Country');
const LotteryEvent = () => import('@/views/lottery-event/LotteryEvent');
const LotteryEvents = () => import('@/views/lottery-event/LotteryEvents');
const LotterySystem = () => import('@/views/lottery-system/LotterySystem');
const LotterySystems = () => import('@/views/lottery-system/LotterySystems');
const Tickets = () => import('@/views/ticket/Tickets');
const Ticket = () => import('@/views/ticket/Ticket');
const TicketAuthorizations = () => import('@/views/ticket-authorization/TicketAuthorizations');
const Audits = () => import('@/views/audit/Audits');
const Audit = () => import('@/views/audit/Audit');
const Firewalls = () => import('@/views/firewall/Firewalls');
const Firewall = () => import('@/views/firewall/Firewall');
const Players = () => import('@/views/player/Players');
const Player = () => import('@/views/player/Player');
const OnlinePlayers = () => import('@/views/online-player/OnlinePlayers');
const OnlinePlayer = () => import('@/views/online-player/OnlinePlayer');
const ThirdPartyPlayers = () => import('@/views/third-party-player/ThirdPartyPlayers');
const ThirdPartyPlayer = () => import('@/views/third-party-player/ThirdPartyPlayer');
const InternalPage = () => import('@/views/internal/InternalPage');

const CashResults = () => import('@/views/reports/cash-result/CashResults');
const CashResult = () => import('@/views/reports/cash-result/CashResult');

const ExpiredWinnings = () => import('@/views/reports/expired-winning/ExpiredWinnings');
const ExpiredWinning = () => import('@/views/reports/expired-winning/ExpiredWinning');

const LotteryOffers = () => import('@/views/reports/lottery-offer/LotteryOffers');
const LotteryOffer = () => import('@/views/reports/lottery-offer/LotteryOffer');

const LotteryResults = () => import('@/views/reports/lottery-results/LotteryResults');
const LotteryResult = () => import('@/views/reports/lottery-results/LotteryResult');

const LotterySystemsPerformance = () => import('@/views/reports/lottery-system-performance/LotterySystemsPerformance');
const LotterySystemPerformance = () => import('@/views/reports/lottery-system-performance/LotterySystemPerformance');

const ReturnPlayers = () => import('@/views/reports/return-player/ReturnPlayers');
const ReturnPlayer = () => import('@/views/reports/return-player/ReturnPlayer');

const TaxedPaidTickets = () => import('@/views/reports/taxed-paid-ticket/TaxedPaidTickets');
const TaxedPaidTicket = () => import('@/views/reports/taxed-paid-ticket/TaxedPaidTicket');

const ExternalContent = () => import('@/views/external-content/ExternalContent');

const Companies = () => import('@/views/company/Companies');
const Company = () => import('@/views/company/Company');

const Products = () => import('@/views/product/Products');
const Product = () => import('@/views/product/Product');

const IncomePayments = () => import('@/views/genreports/income-payment/IncomePayments');
const DeviceIncomePayments = () => import('@/views/genreports/device-income-payment/DeviceIncomePayments');
const DailyMonthlyIncome = () => import('@/views/genreports/daily-monthly-income/DailyMonthlyIncome');
const PrintDailyMonthlyIncome = () => import('@/views/genreports/daily-monthly-income/PrintDailyMonthlyIncome');
const CashRegister = () => import('@/views/genreports/cash-register/CashRegister');
const PrintCashRegister = () => import('@/views/genreports/cash-register/PrintCashRegister');
const ReceiptsWinnings = () => import('@/views/genreports/receipts-winnings/ReceiptsWinnings');
const CashRequired = () => import('@/views/genreports/cash-required/CashRequired');
const ApiImports = () => import('@/views/genreports/api-imports/ApiImports');
const ApiImport = () => import('@/views/genreports/api-imports/ApiImport');
const OnlinePayouts = () => import('@/views/genreports/online-payouts/OnlinePayouts');
const PrintOnlinePayouts = () => import('@/views/genreports/online-payouts/PrintOnlinePayouts');

const Banks = () => import('@/views/bank/Banks');
const Bank = () => import('@/views/bank/Bank');

const Operators = () => import('@/views/operator/Operators');
const Operator = () => import('@/views/operator/Operator');

const Subtypes = () => import('@/views/subtype/Subtypes');
const Subtype = () => import('@/views/subtype/Subtype');

const Slots = () => import('@/views/slot/Slots');
const Slot = () => import('@/views/slot/Slot');

const ShopBalance = () => import('@/views/genreports/shop-balance/ShopBalance');
const ShopBalanceTX = () => import('@/views/genreports/shop-balance/ShopBalanceTx');
const ImageUpload = () => import('@/views/image-upload/ImageUpload');

const CashTransactions = () => import('@/views/cash-transaction/CashTransactions');
const EposTransactions = () => import('@/views/epos-transaction/EposTransactions');

const OnlineCashTransactions = () => import('@/views/online-cash-transaction/OnlineCashTransactions');
const OnlineCashTX = () => import('@/views/online-cash-transaction/OnlineCashTx');

const UserActivity = () => import('@/views/user-activity/UserActivity');
const UserClocking = () => import('@/views/user-clocking/UserClocking');
const TerminalActivity = () => import('@/views/terminal-activity/TerminalActivity');

const ManualReport = () => import('@/views/genreports/manual-report/ManualReport');

const RecomputeShopBalance = () => import('@/views/recompute-shop-balance/RecomputeShopBalance');

const UserClockingManual = () => import('@/views/user-clocking-manual/UserClockingManual');

const OperatorsReport = () => import('@/views/genreports/operators-report/OperatorsReport');
const PrintOperatorsReport = () => import('@/views/genreports/operators-report/PrintOperatorsReport');

const Jackpots = () => import('@/views/jackpots/Jackpots');
const Jackpot = () => import('@/views/jackpots/Jackpot');
const JackpotsHistory = () => import('@/views/jackpots/JackpotsHistory');

const Raffle = () => import('@/views/jackpots/Raffle');
const RaffleSerials = () => import('@/views/jackpots/RaffleSerials');
const ShopVisits = () => import('@/views/reports/shop-visits/ShopVisits')

Vue.use(Router);

const router = new Router({
  mode: 'history', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({
    y: 0
  }),
  routes: [{
    path: '/',
    redirect: '/home',
    name: 'Home',
    component: DefaultContainer,
    children: [{
      path: 'home',
      component: Home,
    },
    {
      path: 'slots',
      meta: {
        label: 'Slots',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Slots,
      },
      {
        path: ':id',
        name: 'Slot',
        component: Slot,
      },
      ]
    },
    {
      path: 'shop-visits',
      meta: {
        label: 'Shop Visits',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ShopVisits,
      },]
    },
    {
      path: 'raffle',
      meta: {
        label: 'Raffle Clients',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Raffle,
      },]
    },
    {
      path: 'raffle-serials',
      meta: {
        label: 'Raffle Serials',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: RaffleSerials,
      },]
    },
    {
      path: 'cash-operations',
      meta: {
        label: 'Cash Operations',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: CashTransactions,
      },]
    },
    {
      path: 'epos-operations',
      meta: {
        label: 'EPOS Operations',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: EposTransactions,
      }]
    },
    {
      path: 'online-cash-transactions',
      meta: {
        label: 'Online Cash Transactions',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: OnlineCashTransactions,
      },
      {
        path: ':id',
        name: 'Online Cash Transaction',
        component: OnlineCashTX,
      },
      ]
    },
    {
      path: 'api-imports',
      meta: {
        label: 'API Imports',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ApiImports,
      },
      {
        path: ':id',
        name: 'API Import',
        component: ApiImport,
      },
      ],
    },
    {
      path: 'online-payouts',
      meta: {
        label: 'Online Payouts',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: OnlinePayouts,
      },
      ],
    },
    {
      path: 'shop-balance',
      meta: {
        label: 'Shop Balance',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ShopBalance,
      },
      {
        path: ':id',
        name: 'Shop Balance Transactions',
        component: ShopBalanceTX,
      },
      ]
    },
    {
      path: 'subtypes',
      meta: {
        label: 'Subtypes',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Subtypes,
      },
      {
        path: ':id',
        name: 'Subtype',
        component: Subtype,
      },
      ]
    },
    {
      path: 'cash-collectors',
      meta: {
        label: 'Cash Collectors',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Operators,
      },
      {
        path: ':id',
        name: 'Cash Collector',
        component: Operator,
      },
      ]
    },
    {
      path: 'banks',
      meta: {
        label: 'Banks',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Banks,
      },
      {
        path: ':id',
        name: 'Bank',
        component: Bank,
      },
      ]
    },
    {
      path: 'users',
      meta: {
        label: 'Users',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Users,
      },
      {
        path: ':id',
        name: 'User',
        component: User,
      },
      ]
    },
    {
      path: 'roles',
      meta: {
        label: 'Roles',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Roles,
      },
      {
        path: ':id',
        name: 'Role',
        component: Role,
      },
      ]
    },
    {
      path: 'permissions',
      name: 'Permissions',
      component: Permissions
    },
    {
      path: 'shops',
      meta: {
        label: 'Shops',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Shops,
      },
      {
        path: ':id',
        name: 'Shop',
        component: Shop,
      },
      ]
    },
    {
      path: 'devices',
      meta: {
        label: 'Devices',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Devices,
      },
      {
        path: ':id',
        name: 'Device',
        component: Device,
      },
      ]
    },
    {
      path: 'shop-groups',
      meta: {
        label: 'Shop Groups',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ShopGroups,
      },
      {
        path: ':id',
        name: 'Shop Group',
        component: ShopGroup,
      },
      ]
    },
    {
      path: 'tenants',
      meta: {
        label: 'Tenants',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Tenants,
      },
      {
        path: ':id',
        name: 'Tenant',
        component: Tenant,
      },
      ]
    },
    {
      path: 'countries',
      meta: {
        label: 'Countries',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Countries,
      },
      {
        path: ':id',
        name: 'Country',
        component: Country,
      },
      ]
    },
    {
      path: 'backoffice',
      name: 'Global Settings',
      component: Registry
    },
    {
      path: 'profile',
      name: 'Profile',
      component: Profile
    },
    {
      path: 'tickets',
      meta: {
        label: 'Tickets',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Tickets,
      },
      {
        path: ':id',
        name: 'Ticket',
        component: Ticket,
      },
      ]
    },
    {
      path: 'ticket-authorizations',
      meta: {
        label: 'Ticket Authorizations',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: TicketAuthorizations,
      }, {
        path: ':id',
        component: TicketAuthorizations,
      }]
    },
    {
      path: 'lottery-events',
      meta: {
        label: 'Lottery Events',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: LotteryEvents,
      },
      {
        path: ':id',
        name: 'Lottery Event',
        component: LotteryEvent,
      },
      ]
    },
    {
      path: 'lottery-systems',
      meta: {
        label: 'Lottery Systems',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: LotterySystems,
      },
      {
        path: ':id',
        name: 'Lottery System',
        component: LotterySystem,
      },
      ]
    },
    {
      path: 'audits',
      meta: {
        label: 'Audits',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Audits,
      },
      {
        path: ':id',
        name: 'Audit',
        component: Audit,
      },
      ]
    },
    {
      path: 'firewalls',
      meta: {
        label: 'Firewall Rulesets',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Firewalls,
      },
      {
        path: ':id',
        name: 'Firewall Ruleset',
        component: Firewall,
      },
      ]
    },
    {
      path: 'players',
      meta: {
        label: 'Players',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',

        component: Players,
      },
      {
        path: ':id',
        name: 'Player',
        component: Player,
      },
      ]
    },
    {
      path: 'online-players',
      meta: {
        label: 'Online Players',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',

        component: OnlinePlayers,
      },
      {
        path: ':id',
        name: 'Online Player',
        component: OnlinePlayer,
      },
      ]
    },
    {
      path: 'third-party-players',
      meta: {
        label: 'Third Party Players',
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',

        component: ThirdPartyPlayers,
      },
      {
        path: ':id',
        name: 'Third Party Player',
        component: ThirdPartyPlayer,
      },
      ]
    },
    {
      path: 'cash-results',
      meta: {
        label: 'Cash Results'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: CashResults
      }, {
        path: ':id',
        name: 'CashResult',
        component: CashResult
      }]
    },
    {
      path: 'expired-winnings',
      meta: {
        label: 'Expired Winnings'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ExpiredWinnings
      }, {
        path: ':id',
        name: 'ExpiredWinning',
        component: ExpiredWinning
      }]
    },
    {
      path: 'lottery-offers',
      meta: {
        label: 'Lottery Offers'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: LotteryOffers
      }, {
        path: ':id',
        name: 'LotteryOffer',
        component: LotteryOffer
      }]
    },
    {
      path: 'lottery-results',
      meta: {
        label: 'Lottery Results'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: LotteryResults
      }, {
        path: ':id',
        name: 'LotteryResult',
        component: LotteryResult
      }]
    },
    {
      path: 'lottery-systems-performance',
      meta: {
        label: 'Lottery Performance'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: LotterySystemsPerformance
      }, {
        path: ':id',
        name: 'LotterySystemPerformance',
        component: LotterySystemPerformance
      }]
    },
    {
      path: 'return-players',
      meta: {
        label: 'Return to Players'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ReturnPlayers
      }, {
        path: ':id',
        name: 'ReturnPlayer',
        component: ReturnPlayer
      }]
    },
    {
      path: 'taxed-paid-tickets',
      meta: {
        label: 'Taxed Paid Tickets'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: TaxedPaidTickets
      }, {
        path: ':id',
        name: 'TaxedPaidTicket',
        component: TaxedPaidTicket
      }]
    },
    {
      path: 'income-payments',
      meta: {
        label: 'Income - Payments'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: IncomePayments
      }]
    },
    {
      path: 'device-income-payments',
      meta: {
        label: 'Terminal Income Reports'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: DeviceIncomePayments
      }]
    },
    {
      path: 'daily-monthly-income',
      meta: {
        label: 'Daily/Monthly Income'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: DailyMonthlyIncome
      }]
    },
    {
      path: 'operators-report',
      meta: {
        label: 'Operators Report'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: OperatorsReport
      }]
    },
    {
      path: 'cash-register',
      meta: {
        label: 'Cash Register'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: CashRegister
      }]
    },
    {
      path: 'income-winnings',
      meta: {
        label: 'Income - Winnings'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ReceiptsWinnings
      }]
    },
    {
      path: 'user-activity',
      meta: {
        label: 'User Activity'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: UserActivity
      }]
    },
    {
      path: 'user-clocking',
      meta: {
        label: 'User Clocking'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: UserClocking
      }]
    },
    {
      path: 'user-clocking-manual',
      meta: {
        label: 'User Clocking (Manual)'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: UserClockingManual
      }]
    },
    {
      path: 'manual-report',
      meta: {
        label: 'Add Manual Financial Report'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: ManualReport
      }]
    },
    {
      path: 'recompute-shop-balance',
      meta: {
        label: 'Recompute Shop Balance'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: RecomputeShopBalance
      }]
    },
    {
      path: 'terminal-activity',
      meta: {
        label: 'Terminal Activity'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: TerminalActivity
      }]
    },
    {
      path: 'cash-required',
      meta: {
        label: 'Cash Required'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: CashRequired
      }]
    },
    {
      path: 'companies',
      meta: {
        label: 'Companies'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Companies
      }, {
        path: ':id',
        name: 'Company',
        component: Company
      }]
    },
    {
      path: 'products',
      meta: {
        label: 'Products'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Products
      }, {
        path: ':id',
        name: 'Product',
        component: Product
      }]
    },
    {
      path: 'external-content/:id',
      meta: {
        label: 'External Content'
      },
      component: ExternalContent
    },
    {
      path: 'jackpots',
      meta: {
        label: 'Jackpots'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: Jackpots
      }, {
        path: ':id',
        name: 'Jackpot',
        component: Jackpot
      }]
    },
    {
      path: 'jackpots-history',
      meta: {
        label: 'History of Jackpots Won'
      },
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [{
        path: '',
        component: JackpotsHistory
      }]
    },
    ]
  },
  {
    path: '/auth',
    redirect: '/auth/login',
    name: 'Pages',
    component: {
      render(c) {
        return c('router-view');
      }
    },
    children: [{
      path: 'login',
      name: 'Login',
      component: Login
    },
    {
      path: '2fa/:id',
      name: '2fa',
      component: TwoFA
    }
    ]
  },
  {
    path: '/print',
    name: 'Print',
    component: {
      render(c) {
        return c('router-view');
      }
    },
    children: [{
      path: 'cash-register',
      name: 'PrintCashRegister',
      component: PrintCashRegister,
    },
    {
      path: 'daily-monthly-income',
      name: 'PrintDailyMonthlyIncome',
      component: PrintDailyMonthlyIncome,
    },
    {
      path: 'operators-report',
      name: 'PrintOperatorsReport',
      component: PrintOperatorsReport,
    },
    {
      path: 'online-payouts',
      name: 'OnlinePyaoutsReport',
      component: PrintOnlinePayouts,
    },
    ]
  },
  {
    path: '/image-upload/:id',
    name: 'Image Upload',
    component: ImageUpload
  },
  {
    path: '/internal-page',
    name: 'Internal Page',
    component: InternalPage
  },
  ]
});

// executed everytime before changing route
router.beforeEach((to, from, next) => {
  NProgress.start();

  store.dispatch('global/setFromURI', from);

  if (store.getters['auth/isAuthenticated']) {
    next();
    return;
  } else if (to.path != "/auth/login" && to.path.indexOf("/auth/2fa") == -1 && to.path.indexOf("/image-upload") == -1) {
    store.dispatch("auth/me")
      .then(() => {
        next();
      })
      .catch(() => {
        next("/auth/login");
      });
    return;
  } else if (to.path == "/auth/login" || to.path.indexOf("/auth/2fa") == 0 || to.path.indexOf("/image-upload") == 0) {
    next();
    return;
  }
  next("/auth/login");
});

// executed everytime after route has changed
router.afterEach((to, from) => {
  NProgress.done();
});

router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    window.location.reload();
  }
});


export default router;
